import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { UserMention } from '@/components/UserMention';
import { UserPicker } from '@/components/UserPicker';
import { useGuildRights } from '@/lib/access';
import { cn } from '@/lib/utils';

/**
 * Bot-command permission grants. Each Discord user in the guild can be granted
 * any of the known command-permission keys — the bot's Interaction handler
 * checks this list before running e.g. /ban.
 *
 * Discord Administrators bypass this list automatically. Admins in the
 * dashboard whitelist edit; MEMBERs only see the current state.
 */

interface Entry {
  userId: string;
  permissions: string[];
}
interface Member {
  id: string;
  displayName: string;
  bot: boolean;
}

function groupPerms(catalog: string[]) {
  const groups = new Map<string, string[]>();
  for (const p of catalog) {
    const key = p.split('.')[0];
    const bucket = groups.get(key) ?? [];
    bucket.push(p);
    groups.set(key, bucket);
  }
  return groups;
}

export function CommandPerms() {
  const { guildId } = useParams<{ guildId: string }>();
  const canEdit = useGuildRights(guildId).can('commandPerms', 'edit');

  const catalogQuery = useQuery({
    queryKey: ['command-perms-catalog', guildId],
    queryFn: () =>
      api<{ catalog: string[] }>(`/api/guilds/${guildId}/command-perms/catalog`),
    enabled: !!guildId,
    staleTime: 5 * 60_000,
  });
  const entriesQuery = useQuery({
    queryKey: ['command-perms', guildId],
    queryFn: () =>
      api<{ entries: Entry[] }>(`/api/guilds/${guildId}/command-perms`),
    enabled: !!guildId,
  });
  const membersQuery = useQuery({
    queryKey: ['members', guildId],
    queryFn: () =>
      api<{ members: Member[] }>(`/api/guilds/${guildId}/members`),
    enabled: !!guildId,
    staleTime: 60_000,
  });

  const catalog = catalogQuery.data?.catalog ?? [];
  const entries = entriesQuery.data?.entries ?? [];
  const groups = useMemo(() => groupPerms(catalog), [catalog]);

  const memberById = useMemo(() => {
    const m = new Map<string, Member>();
    for (const mb of membersQuery.data?.members ?? []) m.set(mb.id, mb);
    return m;
  }, [membersQuery.data]);

  // Users with grants + a placeholder "add new" slot.
  const [addingUserId, setAddingUserId] = useState<string | null>(null);
  const shownEntries = useMemo(() => {
    const list = [...entries];
    if (addingUserId && !list.find((e) => e.userId === addingUserId)) {
      list.push({ userId: addingUserId, permissions: [] });
    }
    return list.sort((a, b) => a.userId.localeCompare(b.userId));
  }, [entries, addingUserId]);

  return (
    <div className="mx-auto max-w-3xl">
      <h1 className="mb-2 text-2xl font-semibold">Command-Berechtigungen</h1>
      <p className="mb-6 text-sm text-muted-foreground">
        Wer darf welche Bot-Slash-Commands im Server ausführen. Discord-Admins
        (Server-Rolle mit Administrator-Flag) dürfen automatisch alles. Alle
        anderen brauchen hier explizite Grants.
      </p>

      {canEdit && (
        <AddUserCard
          guildId={guildId!}
          members={membersQuery.data?.members ?? []}
          existingUserIds={new Set(entries.map((e) => e.userId))}
          onPicked={(id) => setAddingUserId(id)}
        />
      )}

      {catalogQuery.isLoading || entriesQuery.isLoading ? (
        <p className="text-muted-foreground">Lade…</p>
      ) : shownEntries.length === 0 ? (
        <Card>
          <CardContent className="pt-6 text-sm text-muted-foreground">
            Noch keine User mit expliziten Command-Berechtigungen.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {shownEntries.map((e) => (
            <UserPermCard
              key={e.userId}
              guildId={guildId!}
              entry={e}
              member={memberById.get(e.userId)}
              catalog={catalog}
              groups={groups}
              canEdit={canEdit}
              onRemoved={() => setAddingUserId(null)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// --------------------------- Add-User Card ---------------------------

function AddUserCard({
  guildId,
  onPicked,
}: {
  guildId: string;
  members: Member[];
  existingUserIds: Set<string>;
  onPicked: (userId: string) => void;
}) {
  const [selected, setSelected] = useState<string | null>(null);
  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="text-lg">User hinzufügen</CardTitle>
        <CardDescription>
          User suchen (Name oder Discord-ID). Zur User-Liste hinzufügen um Perms zu setzen.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <UserPicker guildId={guildId} value={selected} onChange={setSelected} />
        <div className="flex justify-end">
          <Button
            disabled={!selected}
            onClick={() => {
              onPicked(selected!);
              setSelected(null);
            }}
          >
            Zur Liste hinzufügen
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// --------------------------- Per-User Editor ---------------------------

function UserPermCard({
  guildId,
  entry,
  member,
  catalog,
  groups,
  canEdit,
  onRemoved,
}: {
  guildId: string;
  entry: Entry;
  member: Member | undefined;
  catalog: string[];
  groups: Map<string, string[]>;
  canEdit: boolean;
  onRemoved: () => void;
}) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set(entry.permissions));
  const [dirty, setDirty] = useState(false);

  const saveMut = useMutation({
    mutationFn: () =>
      api(`/api/guilds/${guildId}/command-perms/${entry.userId}`, {
        method: 'PUT',
        body: JSON.stringify({ permissions: [...selected] }),
      }),
    onSuccess: () => {
      setDirty(false);
      qc.invalidateQueries({ queryKey: ['command-perms', guildId] });
      if (selected.size === 0) onRemoved();
    },
  });

  const toggle = (p: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });
    setDirty(true);
  };
  const clearAll = () => {
    setSelected(new Set());
    setDirty(true);
  };
  const selectAll = () => {
    setSelected(new Set(catalog));
    setDirty(true);
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-baseline justify-between gap-3">
          <CardTitle className="text-base">
            <UserMention id={entry.userId} />
          </CardTitle>
          <div className="text-xs text-muted-foreground">
            {selected.size} / {catalog.length} Perms
          </div>
        </div>
        {member && (
          <p className="text-xs text-muted-foreground">
            @{member.displayName}
          </p>
        )}
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {[...groups.entries()].map(([group, perms]) => (
            <div key={group}>
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {group}
              </div>
              <div className="flex flex-wrap gap-1">
                {perms.map((p) => {
                  const on = selected.has(p);
                  return (
                    <button
                      key={p}
                      type="button"
                      disabled={!canEdit}
                      onClick={() => toggle(p)}
                      className={cn(
                        'rounded-md border px-2 py-1 font-mono text-[11px]',
                        on
                          ? 'border-primary bg-primary/15 text-primary'
                          : 'text-muted-foreground hover:bg-accent',
                        !canEdit && 'cursor-not-allowed opacity-70'
                      )}
                    >
                      {p.replace(`${group}.`, '')}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {canEdit && (
          <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={selectAll} disabled={!canEdit}>
              Alle
            </Button>
            <Button variant="ghost" size="sm" onClick={clearAll} disabled={!canEdit}>
              <Trash2 className="mr-1 h-3.5 w-3.5" /> Keine
            </Button>
            <Button
              size="sm"
              disabled={!dirty || saveMut.isPending}
              onClick={() => saveMut.mutate()}
            >
              <Save className="mr-1 h-3.5 w-3.5" />
              {saveMut.isPending ? 'Speichere…' : 'Speichern'}
            </Button>
          </div>
        )}
        {saveMut.error && (
          <p className="mt-2 text-sm text-destructive">
            {(saveMut.error as Error).message}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
